import React, { FunctionComponent } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import {
  DOCUMENT_STATUS,
  DOCUMENT_TYPES,
} from '@savgroup-front-common/constants/shared';
import {
  FileLabel,
  ICONS_TYPE,
  SUPPORTED_COMPONENTS,
} from '@savgroup-front-common/types';

import { Button } from '../../../../atoms/button';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '../../../../formatters';
import { SUPPORTED_ICON_POSITIONS } from '../../../../helpers';
import useDownloadFileDocument from '../../../../hooks/useDownloadFileDocument';
import { useGetCarrierLabelsInfosQuery } from '../../../../hooks/useGetCarrierLabelsInfosQuery';
import { useGetFullClaimContextQuery } from '../../../../hooks/useGetFullClaimContextQuery';
import { DownloadIcon } from '../../../../protons/icons';
import { MyAccountIcon } from '../../../../protons/IconsNewDesign';

import {
  $DocumentIcon,
  $DocumentName,
  $DocumentWrapper,
} from './FileDownloader.styles';
import documentNameByType from './helpers/documentNameByType';
import { useGetDocumentStatusById } from './hooks';
import messages from './messages';

interface FileDownloaderProps {
  isLoading: boolean;
  documentName: string;
  getDocumentStatusByFileId: ({
    fileId,
    type,
  }: {
    fileId: string;
    type: string;
  }) => void;
  fileId: string;
  type: string;
  getDocumentStatusByFileIdValue?: Record<string, string>;
  claimId: string;
  claimGroupId?: string;
  partnerTransportDocumentUrl?: string;
}

const FileDownloader: FunctionComponent<
  React.PropsWithChildren<FileDownloaderProps>
> = ({
  isLoading,
  documentName,
  getDocumentStatusByFileId,
  fileId,
  type,
  getDocumentStatusByFileIdValue,
  claimId,
  partnerTransportDocumentUrl,
}) => {
  useGetDocumentStatusById({ fileId, type, getDocumentStatusByFileId });
  const { fullClaim } = useGetFullClaimContextQuery({ claimId });
  const document = fullClaim?.documents?.find(
    (documents) => documents.name === type,
  );
  const documentStatus = getDocumentStatusByFileIdValue?.[type];

  const {
    carriersLabels: shippinglabelValue,
    isLoading: isShippingLabelsloading,
  } = useGetCarrierLabelsInfosQuery({ fileId }) || [];

  const shippingLabel = shippinglabelValue?.[0] as FileLabel | undefined;

  const isShippingLabel = type === DOCUMENT_TYPES.SHIPPING_LABEL;

  const { handleActionDownloadDocument, handleDownloadDocumentByUrl } =
    useDownloadFileDocument();

  const message = documentNameByType({ type });
  const fallbackFileName = documentName || safeFormattedIntlString(message);

  const shippingLabelFileName =
    shippingLabel?.fileName && shippingLabel?.fileExtension
      ? `${shippingLabel.fileName}.${shippingLabel.fileExtension}`
      : fallbackFileName;

  const documentFileName =
    document?.originalFileName ||
    (document?.name && document?.extension
      ? `${document.name}.${document.extension}`
      : fallbackFileName);

  return (
    <$DocumentWrapper>
      <$DocumentIcon>
        <MyAccountIcon icon={ICONS_TYPE.NEWSPAPER_ICON} />
      </$DocumentIcon>
      <$DocumentName>
        <SafeFormattedMessageWithoutSpread message={documentName || message} />
      </$DocumentName>

      <Button
        onClick={async () => {
          if (isShippingLabel) {
            await handleActionDownloadDocument({
              downloadType: DOCUMENT_TYPES.SHIPPING_LABEL,
              fileId,
              moduleDefinitionId: shippingLabel?.moduleDefinitionId,
              moduleInstanceId: shippingLabel?.moduleInstanceId,
              carrierLabelId: shippingLabel?.carrierLabelId,
              partnerTransportDocumentUrl,
              fileName: shippingLabelFileName,
            });

            return;
          }

          if (documentStatus === DOCUMENT_STATUS.CREATED) {
            await handleDownloadDocumentByUrl({
              documentUrl: document?.url,
              fileName: documentFileName,
            });
          }
        }}
        icon={<DownloadIcon />}
        position={SUPPORTED_ICON_POSITIONS.LEFT}
        type={BUTTON_TYPES.BUTTON}
        small
        secondary
        hollow
        isLoading={
          isLoading ||
          isShippingLabelsloading ||
          (!isShippingLabel && !document)
        }
        disabled={
          documentStatus === DOCUMENT_STATUS.FAILURE ||
          !shippinglabelValue ||
          shippinglabelValue?.length === 0
        }
        componentThemeName={SUPPORTED_COMPONENTS.FILE_DOWNLOADER_BUTTON}
      >
        <SafeFormattedMessageWithoutSpread
          message={messages.downloadDocument}
        />
      </Button>
    </$DocumentWrapper>
  );
};

export default FileDownloader;
